import { z } from "zod";

export const RepairFixSchema = z.object({
	code: z.string(),
	name: z.string(),
	description: z.string(),
	scope: z.string(),
	repeatable: z.boolean(),
});

export const RepairFixListSchema = z.array(RepairFixSchema);

export const RepairCompanySchema = z.object({ id: z.number(), name: z.string() });
export const RepairCompanyListSchema = z.array(RepairCompanySchema);

export const RepairListSchema = z.object({
	id: z.number(),
	name: z.string(),
	createdAt: z.coerce.date(),
});
export const RepairListListSchema = z.array(RepairListSchema);

/**
 * The run report the backend writes onto the event once the job finishes. Every field is optional
 * because a queued run has no report yet, and an older run may predate a field.
 */
export const RepairResultSchema = z
	.object({
		total: z.number().optional(),
		applied: z.number().optional(),
		planned: z.number().optional(),
		failed: z.number().optional(),
		withoutSeniority: z.number().optional(),
		titlesResolved: z.number().optional(),
		unresolvedTitles: z.array(z.string()).optional(),
		seniorityCounts: z.record(z.string(), z.number()).optional(),
	})
	.passthrough();

export const RepairRunSchema = z.object({
	id: z.number(),
	fixCode: z.string(),
	// Null for an instance-wide run. Non-nullable here made a single such row fail the whole
	// RepairRunListSchema parse, so the table rendered empty (PREC-3900).
	companyId: z.number().nullable().optional(),
	listId: z.number().nullable().optional(),
	dryRun: z.boolean(),
	status: z.enum(["queued", "running", "done", "failed"]),
	createdAt: z.coerce.date(),
	finishedAt: z.coerce.date().optional().nullable(),
	result: RepairResultSchema.optional().nullable(),
	error: z.string().optional().nullable(),
});

export const RepairRunListSchema = z.array(RepairRunSchema);

export const RepairEnqueueResultSchema = z.object({
	queued: z.boolean(),
	reason: z.string().optional(),
	eventId: z.number().optional(),
});

export type RepairFix = z.infer<typeof RepairFixSchema>;
export type RepairCompany = z.infer<typeof RepairCompanySchema>;
export type RepairList = z.infer<typeof RepairListSchema>;
export type RepairRun = z.infer<typeof RepairRunSchema>;
export type RepairEnqueueResult = z.infer<typeof RepairEnqueueResultSchema>;
